import { format, differenceInDays } from "date-fns";
import { SubscriptionBadge } from "./SubscriptionBadge";

interface SubscriptionStatusProps {
  subscriptionDetails: {
    status?: string;
    currentPeriodEnd?: string;
  } | null;
  trialEndDate?: string;
}

export const SubscriptionStatus = ({ subscriptionDetails, trialEndDate }: SubscriptionStatusProps) => {
  const getSubscriptionStatus = () => {
    if (subscriptionDetails?.status === "active") return "premium";
    if (!trialEndDate) return "expired";
    
    const now = new Date();
    const trial = new Date(trialEndDate);
    return trial > now ? "trial" : "expired";
  };

  const getDaysRemaining = () => {
    if (!trialEndDate) return 0;
    return differenceInDays(new Date(trialEndDate), new Date());
  };

  const status = getSubscriptionStatus();
  const daysRemaining = getDaysRemaining();

  return (
    <div className="rounded-lg border border-white/10 p-4 bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Subscription Status
        </h3>
        <SubscriptionBadge status={status} />
      </div>

      <div className="space-y-3">
        {status === "premium" && subscriptionDetails && (
          <>
            <p className="text-sm text-foreground/90">
              Status: <span className="capitalize font-medium text-white">{subscriptionDetails.status}</span>
            </p>
            {subscriptionDetails.currentPeriodEnd && (
              <p className="text-sm text-foreground/90">
                Next billing date:{" "}
                <span className="font-medium text-white">
                  {format(new Date(subscriptionDetails.currentPeriodEnd), 'MMMM do, yyyy')}
                </span>
              </p>
            )}
          </>
        )}

        {status === "trial" && (
          <div className="space-y-2">
            <p className="text-sm text-foreground/90">
              Days remaining: <span className="font-medium text-white">{daysRemaining}</span>
            </p>
            <p className="text-sm text-foreground/90">
              Trial ends on:{" "}
              <span className="font-medium text-white">
                {format(new Date(trialEndDate!), 'MMMM do, yyyy')}
              </span>
            </p>
          </div>
        )}

        {status === "expired" && (
          <p className="text-sm text-destructive">
            Your trial has expired. Upgrade now to continue accessing premium features.
          </p>
        )}
      </div>

      {status !== "premium" && (
        <p className="text-xs text-muted-foreground mt-4 border-t border-white/5 pt-3">
          Upgrade to Premium to unlock all features and get priority support.
        </p>
      )}
    </div>
  );
};